import { useNavigate } from 'react-router-dom'
import { Dispatch, SetStateAction, useState, useContext, useEffect } from 'react'

import { MenuItem, TextField } from '@mui/material'

import { flagCodes } from '../../lib'
import { CountryItem } from './country-item'
import { useSocket } from '../../hooks/use-socket'
import { resolveAppAccessToken } from '../../sso/services/app-access.service'
import { isSuiteSsoRedirectMode } from '../../sso/sso-flags'
import { AuthContext, UserContext } from '../../contexts'
import { textFieldWhiteBg } from '../../theme/custom-styles'
import { getCountriesAllowedToOperateData } from '../../api/app-queries.api'
import { parseCountriesAllowedFromStorage } from '../../utils/countries-allowed.utils'

export const CountrySelector = ({ selectedCountry, setSelectedCountry }: { selectedCountry: string; setSelectedCountry: Dispatch<SetStateAction<string>> }) => {
  const navigate = useNavigate()
  const { userSession } = useContext(UserContext)
  const { removeAuth } = useContext(AuthContext)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()

  const [countriesAllowedToOpearate, setCountriesAllowedToOpearate] = useState<Array<string>>(parseCountriesAllowedFromStorage)

  useEffect(() => {
    if (!userSession?._id) {
      return
    }

    void handleUpdateCountriesAllowedToOperate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession?._id])

  useEffect(() => {
    const onSuiteDashboardReady = () => {
      setCountriesAllowedToOpearate(parseCountriesAllowedFromStorage())
    }

    globalThis.addEventListener('suite-dashboard-ready', onSuiteDashboardReady)

    return () => {
      globalThis.removeEventListener('suite-dashboard-ready', onSuiteDashboardReady)
    }
  }, [])

  useEffect(() => {
    if (!appDataSocket) return

    appDataSocket.on('updateSystemUser', async () => {
      handleUpdateCountriesAllowedToOperate()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appDataSocket, isAppDataSocketInitialized])

  const handleUpdateCountriesAllowedToOperate = async () => {
    const storedSelectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'

    if (isSuiteSsoRedirectMode() && globalThis.sessionStorage.getItem('suiteDashboardReady') !== 'true') {
      return
    }

    try {
      const accessToken = await resolveAppAccessToken()
      const { data } = await getCountriesAllowedToOperateData(String(userSession?._id), accessToken)

      if (!Array.isArray(data) || data.length === 0) {
        return
      }

      if (!data.includes(storedSelectedCountry)) {
        handleCloseSession()
        return
      }

      globalThis.localStorage.setItem('_CAO', JSON.stringify(data))
      setCountriesAllowedToOpearate(data)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  if (!countriesAllowedToOpearate.length) {
    return <div style={{ width: '120px' }} aria-hidden />
  }

  const selectValue = countriesAllowedToOpearate.includes(selectedCountry) ? selectedCountry : countriesAllowedToOpearate[0]

  return (
    <div style={{ width: '120px' }}>
      <TextField
        select
        fullWidth
        variant="standard"
        value={selectValue}
        onChange={(event) => {
          const { location } = globalThis
          globalThis.sessionStorage.setItem('selectedCountry', event.target.value)
          setSelectedCountry(event.target.value)
          location.reload()
        }}
        inputProps={{
          style: { textAlign: 'center', justifyContent: 'center', width: 'auto' }
        }}
        sx={{
          ...textFieldWhiteBg,
          '& .MuiSelect-select > div > div': { color: 'white !important', fontWeight: '600' },
          '& .MuiSvgIcon-root': { fill: 'white' },
          '& .MuiInputBase-root .MuiSelect-select': { height: '3em', boxSizing: 'border-box', padding: '12px 16px' }
        }}
      >
        {countriesAllowedToOpearate.map((country: string) => (
          <MenuItem
            key={country}
            value={country}
            sx={{ padding: '15px', justifyContent: 'center', borderBottom: '0.5px solid #eee' }}
          >
            <CountryItem
              code={(flagCodes as { [key: string]: string | TrustedHTML })[country]}
              name={country}
            />
          </MenuItem>
        ))}
      </TextField>
    </div>
  )
}
